import {match} from 'ts-pattern';

import {CheckboxCellRenderer} from '../components/CheckboxCellRenderer/CheckboxCellRenderer';
import {DateCellRenderer} from '../components/DateCellRenderer/DateCellRenderer';
import {DateTimeCellRenderer} from '../components/DateTimeCellRenderer/DateTimeCellRenderer';
import {FileCellRenderer} from '../components/FileCellRenderer/FileCellRenderer';
import {FilePlainCellRenderer} from '../components/FilePlainCellRenderer/FilePlainCellRenderer';
import {FlagCellRenderer} from '../components/FlagCellRenderer/FlagCellRenderer';
import {IntegerCellRenderer} from '../components/IntegerCellRenderer/IntegerCellRenderer';
import {LinkCellRenderer} from '../components/LinkCellRenderer/LinkCellRenderer';
import {MetaTextCellRenderer} from '../components/MetaTextCellRenderer/MetaTextCellRenderer';
import {MoneyCellRenderer} from '../components/MoneyCellRenderer/MoneyCellRenderer';
import {MoneyWithoutDecimalCellRenderer} from '../components/MoneyWithoutDecimalCellRenderer/MoneyWithoutDecimalCellRenderer';
import {PlainCellRenderer} from '../components/PlainCellRenderer/PlainCellRenderer';
import {TagCellRenderer} from '../components/TagCellRenderer/TagCellRenderer';
import {ThumbnailCellRenderer} from '../components/ThumbnailCellRenderer/ThumbnailCellRenderer';
import {TimeIntervalCellRenderer} from '../components/TimeIntervalCellRenderer/TimeIntervalCellRenderer';
import type {EagColumn} from '../types/EagColumn';
import type {RowData} from '../types/RowData';
import {LineHeightToPx} from './lineHeightToValue';

// thumbnailHeightPx is pre-computed in GridCell as rowHeightPx - verticalPaddingPx
// so ThumbnailCellRenderer receives the exact image height it should render at,
// without needing any store subscription of its own.
// Defaults to LineHeightToPx.DEFAULT for callers that don't render thumbnails
// (drag overlay, tests).
export const renderCell = (
  column: EagColumn,
  value: unknown,
  row: RowData,
  thumbnailHeightPx: number = LineHeightToPx.DEFAULT
) =>
  match(column.type)
    .with('MONEY', () => (
      <MoneyCellRenderer
        value={value as {amount: number; currency: string} | null}
        column={column}
        row={row}
      />
    ))
    .with('MONEY_NO_DECIMAL', () => (
      <MoneyWithoutDecimalCellRenderer
        value={value as {amount: number; currency: string} | null}
        column={column}
        row={row}
      />
    ))
    .with('DATE', () => (
      <DateCellRenderer value={value as string | null} column={column} row={row} />
    ))
    .with('DATE_TIME', () => (
      <DateTimeCellRenderer value={value as string | null} column={column} row={row} />
    ))
    .with('LINK', () => (
      <LinkCellRenderer
        value={value as {label: string; url: string} | null}
        column={column}
        row={row}
      />
    ))
    .with('TAG', () => (
      <TagCellRenderer
        value={
          value as Array<{
            label: string;
            style?: {flagColor?: string; flagSubtle?: boolean};
          } | null> | null
        }
        column={column}
        row={row}
      />
    ))
    .with('FILE', () => <FileCellRenderer value={value} column={column} row={row} />)
    .with('FILE_PLAIN', () => <FilePlainCellRenderer value={value} column={column} row={row} />)
    .with('FLAG', () => (
      <FlagCellRenderer
        value={
          value as {
            value: {label: string; key?: string} | Array<{label: string; key?: string}> | null;
            style?: {flagColor?: string; flagSubtle?: boolean};
            id?: string;
          } | null
        }
        column={column}
        row={row}
      />
    ))
    .with('CHECKBOX', () => (
      <CheckboxCellRenderer value={value as boolean | null} column={column} row={row} />
    ))
    .with('THUMBNAIL', () => (
      <ThumbnailCellRenderer
        value={value as string | null}
        column={column}
        row={row}
        thumbnailHeightPx={thumbnailHeightPx}
      />
    ))
    .with('INTEGER', () => (
      <IntegerCellRenderer value={value as number | null} column={column} row={row} />
    ))
    .with('META_TEXT', () => (
      <MetaTextCellRenderer
        value={value as {primary: string; secondary: string} | null}
        column={column}
        row={row}
      />
    ))
    .with('TIME_INTERVAL', () => (
      <TimeIntervalCellRenderer value={value as number | null} column={column} row={row} />
    ))
    .otherwise(() => {
      if (typeof value === 'object' && value !== null) {
        const label =
          'label' in value ? String((value as Record<string, unknown>).label ?? '') : '';
        return <PlainCellRenderer value={label} column={column} row={row} />;
      }
      return <PlainCellRenderer value={String(value ?? '')} column={column} row={row} />;
    });
